import { useEffect, useRef } from 'react';
import { platform } from '../platform/bridge.js';
import { playSound } from '../sound.js';
import { useChatStore } from '../store/chat.js';
import { shouldNotify, useSettingsStore } from '../store/settings.js';
import { useUiStore } from '../store/ui.js';

/** 이보다 많이 쌓이면 현재 목록만 남기고 정리한다. */
const SEEN_LIMIT = 5_000;

/**
 * 안 읽은 개수와 알림.
 *
 * 늘 켜두는 앱이라 창이 뒤에 있을 때 새 말이 온 걸 알려줘야 한다.
 * 단 위장 중에는 아무것도 알리지 않는다 — 화면만 스프레드시트인데
 * 알림 팝업이 뜨거나 소리가 나면 위장한 의미가 없다.
 *
 * 알림 설정(notify)은 '방해'만 통제한다. 팝업과 소리는 설정을 따르지만
 * 안 읽은 개수는 늘 센다 — 개수는 끼어드는 게 아니라 나중에 읽을 거리가
 * 얼마나 쌓였는지 알려주는 정보다. 끄면 아예 몇 개인지도 모르게 되면
 * 오히려 앱을 계속 들여다봐야 한다.
 */
export function useNotifications(): void {
  const stealth = useUiStore((s) => s.view === 'stealth');

  // 렌더링에 쓰이지 않는 값들. 상태로 올리면 메시지마다 리렌더가 돈다.
  const unread = useRef(0);
  /**
   * 이미 셈에 넣은 메시지 id.
   *
   * seq로 판단할 수 없다. 속닥은 저장되지 않아 seq가 0이고, 시간으로
   * 보완하면 몇 초 안에 두 개가 오갈 때 앞의 것이 다시 세어진다.
   * id로 보면 재접속 델타에 같은 메시지가 섞여 와도 안전하다.
   */
  const seen = useRef<Set<string> | null>(null);
  const stealthRef = useRef(stealth);
  stealthRef.current = stealth;

  useEffect(() => {
    if (stealth) {
      // 위장 중에는 아무것도 드러내지 않는다. 개수는 안에서 계속 센다.
      platform.setBadge(0);
      useUiStore.getState().setUnread(0);
      return;
    }

    /*
     * 위장을 풀었다. 화면을 보고 있는 상태라면 그동안 쌓인 것도 방금 읽은
     * 셈이다 — focus 이벤트는 이미 지나갔으므로 여기서 직접 비워야 한다.
     * 안 그러면 눈앞에 대화가 보이는데도 '(4) sokdaq'이 계속 붙어 있다.
     */
    if (!document.hidden && document.hasFocus()) unread.current = 0;

    platform.setBadge(unread.current);
    useUiStore.getState().setUnread(unread.current);
  }, [stealth]);

  useEffect(() => {
    // 창을 다시 보면 읽은 것으로 처리한다.
    const clear = () => {
      if (!document.hidden && document.hasFocus()) {
        unread.current = 0;
        platform.setBadge(0);
        useUiStore.getState().setUnread(0);
      }
    };

    window.addEventListener('focus', clear);
    document.addEventListener('visibilitychange', clear);
    return () => {
      window.removeEventListener('focus', clear);
      document.removeEventListener('visibilitychange', clear);
    };
  }, []);

  useEffect(() => {
    return useChatStore.subscribe((state, previous) => {
      if (state.messages === previous.messages) return;

      const you = state.you;
      if (you === null) return;

      // 처음 입장해 과거 대화를 받아온 것은 새 메시지가 아니다.
      if (seen.current === null) {
        seen.current = new Set(state.messages.map((message) => message.id));
        return;
      }

      const fresh = state.messages.filter((message) => !seen.current!.has(message.id));
      if (fresh.length === 0) return;

      for (const message of fresh) seen.current.add(message.id);
      if (seen.current.size > SEEN_LIMIT) {
        seen.current = new Set(state.messages.map((message) => message.id));
      }

      const notable = fresh.filter(
        (message) =>
          message.userId !== you.userId &&
          message.deletedAt === null &&
          message.kind !== 'system',
      );
      if (notable.length === 0) return;

      unread.current += notable.length;

      // 위장 중에는 개수만 안에서 세고 밖으로는 아무것도 내보내지 않는다.
      if (stealthRef.current) return;

      const settings = useSettingsStore.getState();
      const alerting = notable.filter((message) => shouldNotify(settings.notify, message.kind));

      // 소리는 창을 보고 있어도 낸다. 짧고 작아서 방해가 되지 않고,
      // 오피스 뷰에서 딴 데 보고 있을 때 말이 온 걸 알아채는 유일한 신호다.
      if (settings.sound && alerting.length > 0) {
        playSound(alerting.some((message) => message.kind === 'whisper') ? 'whisper' : 'message');
      }

      const focused = !document.hidden && document.hasFocus();
      if (focused) {
        // 눈앞에 보이는 대화는 읽은 셈이다.
        unread.current = 0;
        return;
      }

      platform.setBadge(unread.current);
      useUiStore.getState().setUnread(unread.current);

      if (alerting.length === 0) return;

      const latest = alerting[alerting.length - 1]!;
      const nickname =
        state.members.find((m) => m.userId === latest.userId)?.nickname ?? '누군가';
      platform.notify(nickname, latest.body);
    });
  }, []);

  // 사람이 들고 나는 소리. 사무실 문 열리는 소리 같은 것으로,
  // 뒤에 켜두고 일할 때 누가 왔는지 알게 해준다.
  useEffect(() => {
    let known: Set<string> | null = null;

    return useChatStore.subscribe((state, previous) => {
      if (state.members === previous.members) return;

      // 방을 나가거나 새로 들어가면 목록이 비워진다. 그걸 '전원 퇴장'으로
      // 읽으면 나가는 순간 퇴장음이 난다. 기준을 다시 잡고 넘어간다.
      if (state.you === null) {
        known = null;
        return;
      }

      const online = new Set(state.members.filter((m) => m.online).map((m) => m.userId));

      // 입장 직후 welcome으로 한꺼번에 받은 목록은 '들어온 것'이 아니다.
      const previouslyOnline = known;
      known = online;
      if (previouslyOnline === null) return;

      const joined = [...online].filter(
        (id) => !previouslyOnline.has(id) && id !== state.you?.userId,
      );
      const left = [...previouslyOnline].filter((id) => !online.has(id));

      if (stealthRef.current) return;

      // 사람이 오가는 것은 '속닥만' 설정이 챙기려는 대상이 아니다.
      // 알림을 좁혔거나 껐다면 이 소리도 내지 않는다.
      const settings = useSettingsStore.getState();
      if (!settings.sound || settings.notify !== 'all') return;

      // 둘이 같이 일어났으면(재접속 정리 등) 입장 쪽만 알린다.
      if (joined.length > 0) playSound('join');
      else if (left.length > 0) playSound('leave');
    });
  }, []);
}
